(ns arthur.ui.timeline
  "The bottom pane: the transport, a ruler, and a row per node of the open symbol
  — the picture's, then, under an `audio` heading, every sound it plays.

  `rows` is the whole of the interesting part and it is a PURE function of the
  clip, the open symbol and the set of open paths. It flattens the document's two
  axes of nesting — parent/child within a symbol, and instance of another
  symbol — into
  one depth-tagged list, which is what lets the labels column and the tracks
  column render from the same vector and therefore stay aligned without measuring
  anything.

  EVERY FRAME NUMBER A ROW CARRIES IS IN THE OPEN SYMBOL'S FRAME SPACE. A node's
  keys are in its own frames, and drawing them against the ruler unmapped would
  put a key under the wrong frame — silently, and most convincingly when the
  node starts at 0. So the walk carries a `->open` function and composes one more
  mapping into it at each level: the inverse of the node's time map,
  `local = rate·(parent − at)`, so `parent = at + local/rate`.

  WHAT IT DOES NOT DO YET: a looping instance repeats its symbol, and only the
  first pass is drawn. An expanded loop therefore shows keys where they first
  happen and not where they happen again."
  (:require [clojure.string :as str]
            [arthur.domain.channel :as channel]
            [arthur.domain.node :as node]
            [arthur.domain.keyframes :as keyframes]
            [arthur.domain.clipboard :as clipboard]
            [arthur.domain.clip :as clip]
            [arthur.domain.nest :as nest]
            [arthur.domain.span :as span]
            [arthur.domain.symbol :as symbol]
            [arthur.events.playback :as pb]
            [arthur.events.project :as project]
            [arthur.events.ui :as ui]
            [arthur.footage.store :as store]
            [arthur.ui.icon :as icon]
            [arthur.subs.playback :as playback]
            [arthur.subs.render :as render]
            [arthur.subs.ui :as sub]
            [arthur.ui.drag :as drag]
            [arthur.ui.layout :as layout]
            [arthur.ui.player :as player]
            [re-frame.core :as rf]
            [re-frame.db :as rf-db]
            [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; the rows

(defn- time->parent
  "The inverse of a time map: where one of its output frames sits in its input."
  [{:keys [at rate]}]
  (if (and (zero? at) (= 1 rate))
    identity
    (fn [f] (+ at (/ f rate)))))

(defn- local->parent
  "Where a frame of a node's OWN time sits in the symbol it lives in.
  See `node/time-of`.

  Two frame spaces meet at every node and mixing them up is the bug this exists
  to prevent: a placement `:at 48` whose scale is keyed at 0 has that key on
  frame 48 of the symbol it is in, and drawing it at 0 puts every placement's
  keys in the same place however staggered they are.

  Cel is not inverted, because it is a floor and has no inverse: a key on a
  frame the exposure grid never samples is still authored on that frame, and that
  is where the row should show it."
  [n]
  (time->parent (node/time-of n)))

(defn- keyed-frames [ch] (some-> (:keys ch) keys sort))

(defn- source-frames
  "How long the thing this node places is, for a placement carrying no span of
  its own. 0 for a node that places nothing, or a reference with nothing there."
  [clip sid n]
  (if-let [{:keys [at rate]} (node/source-time n)]
    (+ at (/ (or (clip/frames clip (node/source n)) 0) rate))
    (clip/frames clip sid)))

(defn- shows
  "Where on the ruler `n` has something to show, as `[from to)`, given `->open`
  from its own frames to the ruler's; nil where it places nothing or shows
  something across all of its span.

  A SPAN IS NOT CONTENT. Lengthening a placement past the end of the symbol it
  plays leaves the bar longer and the picture empty — `node/placed-frame` runs
  out — so the row has to say where the symbol does. A held or looping
  placement always has a frame to show, so only a playing one answers."
  [clip n ->open]
  (when-let [t (and (node/source n) (node/source-time n))]
    (mapv (comp ->open (time->parent t))
          [0 (or (clip/frames clip (node/source n)) 0)])))

(defn- node-label
  "What to call a node in the label column.

  See `clip/node-label`, which this defers to: an instance is labelled by the
  symbol it places, read through so a rename reaches every row that shows it."
  [clip id n]
  (clip/node-label clip id n))

(defn- key-items [n sid ->open]
  (vec (for [[channel ch] (node/channels n) f (keyed-frames ch)]
         {:sid sid :id (:id n) :channel channel :frame f
          :at (->open f) :scale (- (->open 1) (->open 0))})))

(defn- gaps
  "The stretches between one key of a channel and the next, each holding or
  tweening, as Flash draws a classic tween between two keyframes. Nil for a
  channel whose values are choices — a colour, a switch — which only ever hold."
  [n sid path ch ->open]
  (let [fs (keyed-frames ch)]
    (when-not (node/hold-only? path (val (first (:keys ch))))
      (mapv (fn [[a b]]
              {:sid sid :id (:id n) :channel path :left a
               :from (->open a) :to (->open b)
               :tween? (= :linear (channel/segment-interp ch a))})
            (partition 2 1 fs)))))

(defn- channel-rows [n sid path depth ->open span]
  (let [keyed (filter (comp seq :keys val)
                      (sort-by (comp str key) (node/channels n)))]
    (if (seq keyed)
      (for [[cpath ch] keyed]
        {:path   (conj path cpath)
         :depth  depth
         :label  (node/channel-name cpath)
         :kind   :channel
         :select nil
         :span   span
         :keys   (mapv ->open (keyed-frames ch))
         :key-items (filterv #(= cpath (:channel %)) (key-items n sid ->open))
         :gaps   (gaps n sid cpath ch ->open)
         :dense? (boolean (:dense ch))})
      [{:path (conj path ::automation-hint)
        :depth depth
        :kind :hint
        :label "automation would show here if this track had keyframes"}])))

(defn rows
  "The visible rows of symbol `sid`, outermost first. `expanded` is a set of row
  paths, and `chosen` is the selected row's PATH, which is what an expanded
  lane opens as its portal.

  AN EXPANDED LANE OPENS ONE CLIP, NOT ALL OF THEM. A lane of twelve clips that
  grew twelve branches when it opened is the vertical growth the single-row
  lane exists to prevent, so expanding a lane reveals exactly the clip that is
  selected in it: its own keys, and — expanded in turn — the lanes and nodes of
  the symbol it places, all the way down and all mapped into this ruler.
  Selecting another clip swaps the portal in place rather than adding to it, so
  the whole document stays editable from the root timeline at constant cost.
  See `docs/lane-nesting-notes.md`.

  THE WHOLE LINEAGE CHOOSES THE PORTAL, not the selected node alone. Selecting
  something nested — a shape inside the drawing the clip places, or the end of
  its span — is still working inside that clip, so the portal that revealed it
  must stay open. A lane therefore opens the clip whose path the selection is
  under, which for a clip selected directly is the clip itself."
  ([clip sid expanded] (rows clip sid expanded nil))
  ([clip sid expanded chosen]
   (let [under? (fn [path]
                  (and chosen
                       (<= (count path) (count chosen))
                       (= path (subvec (vec chosen) 0 (count path)))))]
  (letfn [(inside-rows [sid n path depth self span]
            ;; The rows of the symbol a clip places, mapped into this ruler.
            ;;
            ;; A HELD CLIP HAS NO INVERTIBLE CLOCK. `node/source-time` is nil
            ;; for a hold, a loop and an endpoint policy: one source frame is
            ;; shown for the whole span, so no frame inside it has a place on
            ;; this ruler. That used to mean the contents were not shown at
            ;; all, which hid the inside of every drawing — the most ordinary
            ;; thing in the document. So the structure is shown and stays
            ;; selectable, and what is withheld is only what cannot be known:
            ;; the key positions, and the bars that would imply them. Refuse
            ;; rather than guess, without refusing the whole subtree.
            (when-let [source (and (= :instance (:kind n)) (node/source n))]
              (if-let [source-time (node/source-time n)]
                (walk source path depth (comp self (time->parent source-time)))
                (mapv (fn [row]
                        (-> row
                            (assoc :keys [] :key-items [] :unmapped? true)
                            (update :cels #(when % (mapv (fn [cel] (assoc cel :keys [] :key-items [])) %)))
                            (assoc :span (when (= :node (:kind row)) span))))
                      (walk source path depth (constantly (first span)))))))
          (portal [sid path depth self child]
            ;; `self` maps the LANE's frames into the open symbol's; `child` is
            ;; the clip selected in that lane, whose own frames are one more
            ;; step in. Its row carries the clip's path — the same one its
            ;; block in the lane selects with — so expanding here and selecting
            ;; there are the same place.
            (let [cpath    (conj path (:id child))
                  open?    (contains? expanded cpath)
                  channels (node/channels child)
                  cself    (comp self (local->parent child))
                  cspan    (mapv self (node/placed-span child))
                  row {:path   cpath
                       :depth  depth
                       :label  (node-label clip (:id child) child)
                       :kind   :node
                       :node-kind (:kind child)
                       :of     (node/source child)
                       :uses   (some->> (node/source child) (clip/uses clip))
                       :portal? true
                       :select [:node sid (:id child) cpath]
                       :expandable? true
                       :expanded?   open?
                       :span   cspan
                       :shows  (shows clip child cself)
                       :key-items (key-items child sid cself)
                       :keys   (into [] (comp (mapcat keyed-frames)
                                              (map cself)
                                              (distinct))
                                     (vals channels))
                       :dense? (boolean (some :dense (vals channels)))}]
              (if-not open?
                [row]
                (-> [row]
                    (into (channel-rows child sid cpath (inc depth) cself cspan))
                    (into (inside-rows sid child cpath (inc depth) cself cspan))))))
          (walk [sid path depth ->open]
            (let [sym (get-in clip [:symbols sid])
                  root-lane? (and (empty? path) (symbol/lane? sym))
                  root-clips (when root-lane? (symbol/children (:nodes sym)))
                  root-ids (into #{} (map :id) root-clips)
                  ordered (->> (:nodes sym)
                               ;; Front-most at the top, as a layer list is drawn
                               ;; everywhere. `:z` is the lexicographic draw key;
                               ;; the id breaks ties so the order is stable.
                               (sort-by (fn [[id n]] [(or (:z n) "") (str id)]))
                               reverse
                               ;; Sounds are listed below the picture, by
                               ;; `sound-rows`, wherever they are.
                               (remove #(= :audio (:kind (val %))))
                               ;; In an open lane symbol, its clips are blocks
                               ;; on the symbol's own row. Span-less decoration
                               ;; remains an ordinary row beside it.
                               (remove #(contains? root-ids (key %))))
                  root-row (when root-lane?
                             (let [open? (contains? expanded [::lane])
                                   row {:path [::lane]
                                        :depth depth
                                        :label (or (:name sym) (name sid))
                                        :kind :symbol
                                        :lane? true
                                        :select [:node sid nil []]
                                        :expandable? true
                                        :expanded? open?
                                        :span [0 (:frames sym)]
                                        :keys []
                                        :cels (mapv (fn [child]
                                                      {:id (:id child)
                                                       :label (or (get-in clip [:symbols (node/source child) :name])
                                                                  (some-> (node/source child) name)
                                                                  (node-label clip (:id child) child))
                                                       :source (node/source child)
                                                       :span (mapv ->open (node/placed-span child))
                                                       :shows (shows clip child (comp ->open (local->parent child)))
                                                       :key-items (key-items child sid (comp ->open (local->parent child)))
                                                       :keys (into []
                                                                   (comp (mapcat keyed-frames)
                                                                         (map (comp ->open (local->parent child)))
                                                                         (distinct))
                                                                   (vals (node/channels child)))
                                                       :select [:node sid (:id child)
                                                                (conj path (:id child))]})
                                                    root-clips)}]
                               (cond-> [row]
                                 open?
                                 (into (if-let [child (first (filter #(under? (conj path (:id %)))
                                                                     root-clips))]
                                         (portal sid path (inc depth) ->open child)
                                         [{:path [::lane ::portal]
                                           :depth (inc depth) :kind :hint
                                           :label (if (seq root-clips)
                                                    "select a clip to inspect"
                                                    "empty lane")}])))))]
              (into (vec root-row)
                    (mapcat
                     (fn [[id n]]
                       (let [rpath    (conj path id)
                             open?    (contains? expanded rpath)
                             channels (node/channels n)
                             ;; The span is in the PARENT's space and the channels
                             ;; are in the node's own, so they take different
                             ;; mappings. `self` is also what the instance's
                             ;; symbol is resolved in — `clip/resolver` roots the
                             ;; child at this node's local frame — so the nested
                             ;; walk carries it down unchanged.
                             ancestors (rest (symbol/lineage (:nodes sym) id))
                             parent-map (reduce comp ->open
                                                (map #(local->parent (get-in sym [:nodes %]))
                                                     (reverse ancestors)))
                             self     (comp parent-map (local->parent n))
                             source-sym (when (= :instance (:kind n))
                                          (clip/symbol clip (node/source n)))
                             ;; `self` maps the instance's local clock. Rows of
                             ;; the symbol it places use its source clock too.
                             source-self (if-let [t (and source-sym
                                                         (node/source-time n))]
                                           (comp self (time->parent t))
                                           self)
                             lane?    (symbol/lane? source-sym)
                             clips    (when lane? (symbol/children (:nodes source-sym)))
                             span     (mapv parent-map
                                            (or (node/placed-span
                                                 (cond-> n
                                                   (and (= :instance (:kind n)) (nil? (:span n)))
                                                   (assoc :span [0 (source-frames clip sid n)])))
                                                [0 (:frames sym)]))
                             row {:path   rpath
                                  :depth  depth
                                  :label  (node-label clip id n)
                                  :kind   :node
                                  :node-kind (:kind n)
                                  :lane? lane?
                                  :of     (node/source n)
                                  :uses   (some->> (node/source n) (clip/uses clip))
                                  :select [:node sid id rpath]
                                  :expandable? true
                                  :expanded?   open?
                                  :span   span
                                  :shows  (shows clip n self)
                                  :key-items (key-items n sid self)
                                  :keys   (into [] (comp (mapcat keyed-frames)
                                                         (map self)
                                                         (distinct))
                                                (vals channels))
                                  :dense? (boolean (some :dense (vals channels)))}]
                         (let [;; A lane of sounds is a lane like any other —
                                 ;; same blocks, same edges, same portal — and
                                 ;; is listed under the audio heading because
                                 ;; that is where somebody looks for a sound,
                                 ;; not because it is a different kind of row.
                                 sound-lane? (and (seq clips)
                                                  (every? #(= :audio (:kind %)) clips))
                                 row (cond-> row
                                       lane?
                                       (assoc :cels
                                              (mapv (fn [child]
                                                      {:id (:id child)
                                                       :label (or (get-in clip [:symbols (node/source child) :name])
                                                                  (some-> (node/source child) name))
                                                       :source (node/source child)
                                                       :span (mapv source-self (node/placed-span child))
                                                       :shows (shows clip child (comp source-self (local->parent child)))
                                                       ;; The clip's own keys, on the
                                                       ;; block, so a collapsed lane
                                                       ;; still says where it changes.
                                                       :key-items (key-items child (node/source n) (comp source-self (local->parent child)))
                                                       :keys (into []
                                                                   (comp (mapcat keyed-frames)
                                                                         (map (comp source-self (local->parent child)))
                                                                         (distinct))
                                                                   (vals (node/channels child)))
                                                       :select [:node (node/source n) (:id child)
                                                                (conj rpath (:id child))]})
                                                    clips)))]
                             (cond->> (if-not open?
                                        [row]
                                        (-> [row]
                                            (into (channel-rows n sid rpath (inc depth) self span))
                                            ;; The one clip an expanded lane opens.
                                            (into (when lane?
                                                    (if-let [child (first (filter #(under? (conj rpath (:id %))) clips))]
                                                      (portal (node/source n) rpath (inc depth) source-self child)
                                                      [{:path (conj rpath ::portal)
                                                        :depth (inc depth)
                                                        :kind :hint
                                                        :label (if (seq clips)
                                                                 "select a clip to inspect"
                                                                 "empty lane")}])))
                                            (into (when-not lane?
                                                    (inside-rows sid n rpath (inc depth) self span)))))
                               sound-lane? (mapv #(assoc % :sound? true))))))
                     ordered))))]
    (if (get-in clip [:symbols sid])
      ;; IDENTITY AT THE TOP. The ruler is this symbol's own frames, so a row of
      ;; its own children needs no mapping at all; `->open` only earns its keep
      ;; further down, where a row reached through an instance is in some other
      ;; symbol's frames.
      (walk sid [] 0 identity)
      [])))))

(defn palette-track-rows
  "`sid`'s palette rows. Its row: ONE LANE ROW WHETHER OR NOT THE LANE EXISTS YET, because
  a person putting a palette transition somewhere is doing the same thing in
  either case. Empty, it names the symbol whose palette it would be; filled, it
  is the lane symbol's own row under this pane's name for it, and opens as any
  lane does: onto the selected transition, its keys and channels."
  [clip sid frames expanded chosen]
  (let [track-id (get-in clip [:symbols sid :palette-track])
        track (get-in clip [:symbols track-id])]
    (if (= :palette-track (:type track))
      ;; A MATERIALIZED TRACK IS AN ORDINARY LANE ROW, selection, portal and
      ;; all: it is what `rows` already draws for a lane symbol, wearing this
      ;; pane's name for it. Only the row of a track that does not exist yet has
      ;; to say whose palette it would be, which is what creating in it needs.
      ;;
      ;; `rows` calls a lane's own row `[::lane]`, which is also the open
      ;; symbol's when that is a lane, so this row goes by `[::palette-track]`
      ;; and is translated on the way in. The portal's rows keep their authored
      ;; paths -- the clip ids are the track's own -- and carry the track as
      ;; their `:owner`, so dragging them slides the clip in the track.
      (let [expanded (cond-> (disj (set expanded) [::lane])
                       (contains? (set expanded) [::palette-track]) (conj [::lane]))
            [lane & more] (rows clip track-id expanded chosen)]
        (into [(assoc lane :path [::palette-track] :owner track-id
                      :label "palette" :kind :palette)]
              (comp (take-while #(pos? (:depth %)))
                    (map #(cond-> (assoc % :owner track-id)
                            (= ::lane (first (:path %)))
                            (assoc :path (into [::palette-track] (rest (:path %)))))))
              more))
      [{:path [::palette-track] :depth 0 :label "palette" :kind :palette
        :lane? true :owner nil :select [::palette-track sid]
        :span [0 frames] :keys [] :cels []}])))

(defn sound-rows
  "Audio rows use the same flattened intervals as the mixer, including source
  in-points, cel speeds, parent timing, and silence beneath visual holds.

  WHAT IS IN A LANE SYMBOL IS NOT FLATTENED HERE. A sound in a lane is
  a clip somebody placed and can move, trim and open, and `rows` draws it as
  one; flattening it as well would show the same sound on two rows, only one of
  which could be edited. What remains is what this view is for: audio nested
  inside the symbols this one places, mapped into this ruler."
  [clip sid expanded]
  (if-not (get-in clip [:symbols sid]) []
    ;; `nest/audio-tracks` is the mixer's own flattening. A sound's bar may land
    ;; between two marks, and that is honest: a sound does not have to start on
    ;; a frame boundary.
    (let [lane? (symbol/lane? (clip/symbol clip sid))]
    (vec
     (mapcat
      (fn [[path tracks]]
        (let [n (first tracks)
              authored (get-in clip [:symbols (:owner n) :nodes (:id n)])
              authored->open (local->parent n)
              span (node/placed-span n)
              select [:node (:owner n) (:id n) path]
              open? (contains? expanded path)
              via (when (< 1 (count path)) (str (first path)))
              row {:path path :depth 0 :label (node-label clip (:id n) n)
                   :kind :node :node-kind :audio :via via
                   :key-items (key-items authored (:owner n) authored->open)
                   :slides (if via (subvec path 0 1) path)
                   :select select :expandable? true :expanded? open? :span span
                   :keys (vec (distinct (mapcat keyed-frames (vals (:channels n)))))}]
          (cons (cond-> row
                  (< 1 (count tracks))
                  (assoc :cels (mapv (fn [i track]
                                      {:id i :label (node-label clip (:id track) track)
                                       :span (node/placed-span track)
                                       :select select})
                                    (range) tracks)))
                (when open? (channel-rows authored (:owner n) path 1 authored->open span)))))
      (sort-by (comp str key)
               (group-by :path
                         (remove #(and lane? (= 1 (count (:path %))))
                                 (nest/audio-tracks clip sid)))))))))

;; ---------------------------------------------------------------------------
;; geometry
;;
;; Percentages, so nothing has to measure the track column. A frame f occupies
;; [f/frames, (f+1)/frames), and a mark that names one frame sits at its centre.

(defn- at% [f frames] (str (* 100 (/ (+ f 0.5) (max 1 frames))) "%"))
(defn- edge% [f frames] (str (* 100 (/ f (max 1 frames))) "%"))

(defn- frame-under
  "Which frame the pointer is over, measured in `element`'s box.

  THE ONE ANSWER EVERY GESTURE IN THIS PANE IS BUILT FROM, and the only place a
  pixel becomes a frame. A drop lands on it, a scrub seeks to it, and a drag's
  distance is the difference between two of its answers -- so the preview and the
  commit cannot disagree, because they are the same subtraction. The alternative,
  which this replaces, was a pixel delta scaled and rounded on its own: it
  answered 1 where the pointer had crossed two marks and the ghost drew one
  number while the command wrote another.

  Clamped into the ruler: a frame off either end is not a frame, and a gesture
  dragged past the end and back again should come back to the frame it is over."
  [^js event frames ^js element]
  (let [box (.getBoundingClientRect element)
        x   (- (.-clientX event) (.-left box))]
    (-> (/ (* x frames) (.-width box)) js/Math.floor (max 0) (min (dec frames)))))

(defn- frame-at
  "`frame-under`, in the box of the element whose handler is asking."
  [^js event frames]
  (frame-under event frames (.-currentTarget event)))

(defn- lane-under
  "The lane track geometrically under a captured pointer, and its selection."
  [^js event]
  (some (fn [^js el]
          (when-let [track (.closest el ".tl-track")]
            (when-let [selection (aget track "arthurLane")]
              [track selection])))
        (array-seq (.elementsFromPoint js/document (.-clientX event) (.-clientY event)))))

(defn- clip-under
  "What the clip block under the pointer places, or nil where there is none.

  The track holds the pointer while a bar slides, and a captured pointer takes
  the CLICK with it: press a clip block and the click and double-click that
  follow are delivered to the track, never to the block. So the track resolves
  them itself, by asking what is geometrically under the pointer."
  [^js event]
  (some (fn [^js el]
          (when-let [cel (.closest el ".tl-cel")]
            (aget cel "arthurCel")))
        (array-seq (.elementsFromPoint js/document (.-clientX event) (.-clientY event)))))

(defn stops
  "The frames ↑ and ↓ stop on: every key and every clip edge the timeline shows,
  as frames of the open symbol. In a lane that is drawing to drawing, on a tween
  key to key — one rule, read off the rows the timeline draws, so a jump lands
  on something you can see."
  [db]
  (let [{document :clip} (store/entry (:clip/current db))
        selection (get-in db [:ui :selection])
        n (pb/frames db)]
    (into (sorted-set)
          (comp (mapcat (fn [{:keys [keys span cels]}]
                          (concat keys span (mapcat (juxt :span :keys) cels))))
                (mapcat #(if (number? %) [%] %))
                ;; The first whole frame that shows it, forgiving the rounding
                ;; a mapped key picks up on its way through a clock.
                (map #(js/Math.ceil (- % 1e-6)))
                (filter #(< -1 % n)))
          (when document
            (rows document (get-in db [:ui :open]) (get-in db [:ui :expanded])
                           (when (= :node (first selection)) (nth selection 3)))))))

(defn- at [db] (get-in db [:playback :frame] 0))

;; WHAT A KEY AND ITS BUTTON BOTH SEND, as functions of the db answering the
;; event, or nil for nothing to do. `ui/shortcuts` puts them on keys and the
;; toolbar on buttons, so the two cannot come to mean different things.

(defn prev-key [db] (some->> (first (rsubseq (stops db) < (at db))) (vector ::pb/seek)))
(defn next-key [db] (some->> (first (subseq (stops db) > (at db))) (vector ::pb/seek)))
(defn last-frame [db] [::pb/seek (dec (pb/frames db))])

(defn key-here
  "K: in a lane, a new drawing from the playhead on, copying the one there; on
  anything else, a key of what is selected."
  [db]
  (if (ui/lane-here db) [::ui/drawing-here true] [::ui/key-selected]))

(defn hold
  "= and −: the end of the selection's span, `delta` frames later."
  [delta]
  (fn [db] (when-let [p (ui/span-path db)] [::ui/slide p delta :out true])))

(defn- places-lane?
  "Does the row at `path` in the open symbol place a lane? Holding a lane
  longer is not lengthening a drawing, which is what = and − are on the bar for."
  [db path]
  (let [{document :clip st :store} (store/entry (:clip/current db))
        f (ui/editing-frame db document)
        host (nest/inside document st (get-in db [:ui :open]) (pop path) f)
        n (get-in document [:symbols (:sid host) :nodes (peek path)])]
    (boolean (some->> (node/source n) (clip/symbol document) symbol/lane?))))

(rf/reg-sub
 ::here
 ;; Which of the toolbar's commands mean something for the selection right now.
 (fn [db _]
   (let [p (ui/span-path db)]
     {:lane? (some? (ui/lane-here db))
      :span? (and (some? p) (not (places-lane? db p)))})))

;; ---------------------------------------------------------------------------
;; the panes

(def ^:private speeds [[0.25 "¼×"] [0.5 "½×"] [1 "1×"] [2 "2×"] [4 "4×"]])

(defn- transport []
  (let [playing? @(rf/subscribe [::playback/playing?])
        rate     @(rf/subscribe [::playback/rate])
        loop?    @(rf/subscribe [::playback/loop?])
        muted?   @(rf/subscribe [::playback/muted?])
        auto?    @(rf/subscribe [::sub/auto-key?])
        frame    @(rf/subscribe [::playback/frame])
        frames   @(rf/subscribe [::render/frames])
        {:keys [fps drop]} @player/meter
        clip @(rf/subscribe [::render/clip])
        clip-id @(rf/subscribe [::render/clip-id])
        selection @(rf/subscribe [::sub/selection])
        [_ sid id] selection
        open @(rf/subscribe [::render/open])
        st (:store (store/entry clip-id))
        n (get-in clip [:symbols sid :nodes id])
        ;; A SPAN IS A SPAN WHEREVER IT SITS. What `span/split`, `trim` and
        ;; `move` need is one node with a place in time, which a symbol dropped
        ;; straight into a shot has as surely as a cel does — so these are
        ;; offered on both, and a GROUP is the exception rather than a lane
        ;; being the rule: dividing one means deciding what becomes of its
        ;; children, and nothing in a span says.
        span? (and n (not= :group (:kind n)) (some? (node/placed-span n)))
        ;; Every one of them acts at the playhead, so every one is offered only
        ;; where the playhead is somewhere it means something -- and in the open
        ;; symbol's own frames, which is what the commands take. `frame` and
        ;; `frames` above are the transport's own numbers and are only read out.
        owner-frame (ui/selection-frame clip st open selection frame)
        ;; TWO COORDINATES AND THEY ARE NOT THE SAME. `host` is the frame of
        ;; whatever the selection is positioned in — its lane, for a cel; the
        ;; symbol, for a free placement — and is what the span commands take.
        ;; `at` is the LANE's own frame, which is what the lane commands take and
        ;; which only exists when there is a lane.
        host (when (number? owner-frame) (span/host-frame clip sid id owner-frame))
        cuttable? (and span? (integer? host)
                       (let [[lo hi] (node/placed-span n)] (< lo host hi)))
        act (fn [event] #(rf/dispatch event))
        ;; A command that reads the db when it is pressed, as a key does.
        run (fn [f] #(some-> (f @rf-db/app-db) rf/dispatch))
        here @(rf/subscribe [::here])
        marks @(rf/subscribe [::playback/range])
        selections @(rf/subscribe [::sub/selections])
        clipboard @(rf/subscribe [::sub/clipboard])
        ;; A peg goes over ONE node: it takes that node's place in the
        ;; hierarchy, and there is no one place a pair of them would go.
        one-node (when (= 1 (count selections))
                   (let [[kind _ _ path] (first selections)]
                     (when (and (= :node kind) (seq path)) path)))]
    [:div.pane-head
     ;; ----------------------------------------------------------------- time
     ;; Where the playhead is and how it moves. Nothing here depends on a
     ;; selection, which is why it is the one group that is never greyed.
     ;; Blender's timeline header: the ends, the keys, a frame, play.
     [:div.group
      [:button.ico {:aria-label "go to start" :title "go to the first frame (Home)"
                    :on-click (act [::pb/seek 0])} [icon/view :start]]
      [:button.ico {:aria-label "previous key" :title "back to the key or drawing before (↓ or S)"
                    :on-click (run prev-key)} [icon/view :prev]]
      [:button {:aria-label "−1" :title "step back one frame (← or A)"
                :on-click (act [::pb/step -1])} "−1"]
      [:button.ico.ico-play {:aria-label (if playing? "pause" "play")
                             :title (if playing? "pause (Space)" "play (Space)")
                             :on-click (act [::pb/toggle])}
       [icon/view (if playing? :pause :play)]]
      [:button {:aria-label "+1" :title "step forward one frame (→ or D)"
                :on-click (act [::pb/step 1])} "+1"]
      [:button.ico {:aria-label "next key" :title "on to the key or drawing after (↑ or W)"
                    :on-click (run next-key)} [icon/view :next]]
      [:button.ico {:aria-label "go to end" :title "go to the last frame (End)"
                    :on-click (run last-frame)} [icon/view :end]]]
     [:div.group
      [:button.ico {:class (when loop? "on") :aria-label "loop"
                    :title "loop playback" :on-click (act [::pb/toggle-loop])}
       [icon/view :loop]]
      [:button.ico {:class (when muted? "on") :aria-label (if muted? "unmute" "mute")
                    :title (if muted? "unmute" "mute") :on-click (act [::pb/toggle-mute])}
       [icon/view (if muted? :muted :sound)]]]
     ;; Beside the transport rather than off at the right margin: it is what you
     ;; read WHILE scrubbing, and tabular figures keep it from twitching as it
     ;; counts.
     [:span.readout.at-frame (str frame " / " frames)]
     ;; ONE BUTTON THAT SAYS THE SPEED AND STEPS IT, rather than a menu: it is
     ;; 1× almost always, and a click is all the other four are away. Shift goes
     ;; back. playbackRate on the audio element and nothing else: the sound
     ;; slows, currentTime advances proportionally, and the derived frame
     ;; follows. Slow motion cannot desync by construction.
     (let [i (or (first (keep-indexed #(when (= rate (first %2)) %1) speeds)) 2)]
       [:button.speed {:class (when (not= 1 rate) "on") :aria-label "playback speed"
                       :title "playback speed · click for faster, shift-click for slower"
                       :on-click (fn [^js e]
                                   (let [j (mod (+ i (if (.-shiftKey e) -1 1)) (count speeds))]
                                     (rf/dispatch [::pb/set-rate (first (nth speeds j))])))}
        (second (nth speeds i))])
     ;; Resolve's I and O: where playback loops in the open tab. Lit while set,
     ;; and shift takes one away, as on the keys.
     [:div.group {:aria-label "loop marks"}
      (doall
       (for [[which word k] [[:in "in" "I"] [:out "out" "O"]]]
         ^{:key which}
         [:button {:class (when (get marks which) "on")
                   :title (str "mark " word " at the playhead (" k ")"
                               " · shift-click clears it (⇧" k ")")
                   :on-click (fn [^js e]
                               (rf/dispatch (if (.-shiftKey e)
                                              [::pb/clear-mark which]
                                              [::pb/mark which])))}
          word]))]
     [:span.sep]
     ;; WHAT THE SELECTION CAN HAVE DONE TO IT, and nothing else. A drawing in
     ;; a lane takes a new drawing and a longer hold; a symbol placed anywhere
     ;; takes a cut and a longer span; a node takes a peg. Shown, not
     ;; greyed, so the strip says what is possible here, and each says its key.
     (let [{:keys [lane?] hold? :span?} here]
       [:div.group.context-controls {:aria-label "selection"}
        ;; In a lane, K is a new drawing, which is Flash's F6 and has nothing
        ;; to do with a channel's key. Keying a node stays on the key: auto-key
        ;; and the inspector's marks already do it where the value is.
        (when lane?
          [:<>
           [:button {:title "a new drawing from the playhead on, carrying on the one there (K)"
                     :on-click (run key-here)}
            [icon/view :drawing] " drawing"]
           [:button {:title "a blank drawing from the playhead on (⇧K)"
                     :on-click (act [::ui/drawing-here false])}
            [icon/view :blank] " blank"]])
        (when hold?
          [:<>
           [:button {:aria-label "one frame shorter" :title "one frame shorter (−)"
                     :on-click (run (hold -1))} "−"]
           [:button {:aria-label "one frame longer" :title "one frame longer (=)"
                     :on-click (run (hold 1))} "="]])
        (when cuttable?
          [:button {:title "split the selected clip at the playhead"
                    :on-click (act [::ui/split])} "split"])
        ;; THE CLIPBOARD, which has keys everywhere but a phone. Copy, cut and
        ;; duplicate while something is selected, paste while something is
        ;; copied.
        (when (seq selections)
          [:<>
           [:button.ico {:aria-label "copy" :title "copy (⌘C)"
                         :on-click (act [::ui/copy])} [icon/view :copy]]
           [:button.ico {:aria-label "cut" :title "cut (⌘X)"
                         :on-click (act [::ui/cut])} [icon/view :cut]]
           [:button.ico {:aria-label "duplicate" :title "duplicate beside it (⌘D)"
                         :on-click (act [::ui/duplicate])} [icon/view :duplicate]]])
        (when clipboard
          [:button.ico {:aria-label "paste" :title "paste at the playhead (⌘V)"
                        :on-click (act [::ui/paste])} [icon/view :paste]])
        (when (and one-node (not lane?))
          ;; The only way to keep a pivot, and the only way to transform a
          ;; measured part by hand — which is what `gesture/refusal` tells you
          ;; to do, so it has to be reachable from somewhere.
          [:button {:title "a transform node over the selection · ⌃/⌘-drag its cross to place the pivot"
                    :on-click (act [::ui/peg one-node])} "peg"])])
     [:span.spacer]
     [:div.group.creation-controls {:aria-label "create"}
      [:button {:title "create a symbol in the selected location (N)"
                :on-click (act [::ui/new-symbol :inside])}
       [icon/view :symbol] " + symbol"]
      [:button {:title "create a lane in the selected location (⇧N)"
                :on-click (act [::ui/new-lane])}
       [icon/view :lane] " + lane"]]
     [:button.auto-key {:class (when auto? "on")
                        :aria-pressed auto?
                        :title (if auto?
                                 "auto-key is on — edited parameters get a key at this frame"
                                 "auto-key edited parameters at this frame")
                        :on-click (act [::ui/toggle-auto-key])}
      "◆ auto key"]
     [:span.sep]
     ;; How many pixels a frame is. The tracks are positioned in percentages of
     ;; their own width, so this is one custom property on the grid and no
     ;; arithmetic here — see `ui/layout`.
     [layout/zoomer :tl "the timeline"]
     [:span.sep]
     ;; After the spacer, both of them: an offer that appears and a reading that
     ;; comes and goes must not shove the fixed controls sideways when they do.
     (when @(rf/subscribe [::sub/retry])
       [:button.retry {:title "the command was refused because the shot is too short"
                       :on-click (act [::ui/retry])} "extend shot and apply"])
     ;; Measured in the loop, not derived from the clock — the whole question
     ;; while profiling is whether the painting keeps up with the clock, and a
     ;; number computed FROM the clock would answer itself. Shown only while it
     ;; has something to say: paused, it read "0.0 paint/s" for ever.
     (when (pos? (or fps 0))
       [:span.readout {:class (if (and drop (> drop 1.35)) "warn" "dim")}
        (str (.toFixed fps 1) " paint/s"
             (when (and drop (pos? drop)) (str " · " (.toFixed drop 2) " f/paint")))])]))

(defn- takes?
  "Whether the row at `target`, a `target-kind` node, can take the row being
  carried: not itself, and not anything inside it. A sound goes into a
  placement or beside another sound, and only a sound goes beside a sound."
  [target target-kind]
  (when-let [from (drag/row)]
    (and (every? (fn [source]
                   (not= source (subvec target 0 (min (count source) (count target)))))
                 (if-let [many (seq (drag/row-selections))]
                   (map #(nth % 3) many) [from]))
         (if (= :audio (drag/row-kind))
           (#{:audio :instance} target-kind)
           (not= :audio target-kind)))))

(defn- zone
  "Which part of a row the pointer is over: its top edge, to go in front of it;
  its bottom edge, to go behind; its middle, to go into it or be grouped with it.
  Nothing goes into a sound, so its middle is behind it."
  [^js e node-kind]
  (let [box (.getBoundingClientRect (.-currentTarget e))
        y   (/ (- (.-clientY e) (.-top box)) (max 1 (.-height box)))]
    (cond (< y 0.3) :front
          (or (> y 0.7) (= :audio node-kind)) :back
          :else :into)))

(def ^:private reveal
  "A ref that scrolls the selected row into view, ONE per selection: React calls
  a ref again only when it is a different function, so a row is scrolled to when
  it becomes the selected one — from the stage, maybe, five rows down — and not
  on every render after, which would fight a person scrolling away."
  (memoize (fn [_selection] (fn [el] (some-> el (.scrollIntoView #js {:block "nearest"}))))))

(defn- label-cell [{:keys [path depth label kind node-kind lane? span select expandable? expanded? of via uses]}
                   selection selections target-path over solo tracing renaming draft choose-row!]
  (let [node? (= :node kind)
        ;; The palette row is the symbol's one palette lane: "palette" says all
        ;; there is to say, and it has no name of its own to change.
        named-lane? (and lane? (not= :palette kind))
        ;; A node row renames what it shows: an instance, its symbol.
        renamable? (or named-lane? (and node? (some? (nth select 2 nil))))
        selected? (contains? selections select)
        editing? (and renamable? (= select @renaming))
        begin-rename! (fn [] (reset! draft label) (reset! renaming select))
        commit-rename! (fn []
                         (when (= select @renaming)
                           (reset! renaming nil)
                           (let [[_ sid id] select]
                             (rf/dispatch [::ui/rename-node sid id @draft]))))
        [over-path where] @over]
    [:div (cond-> {:class (str "tl-label" (when (and select selected?) " on")
                               (when (and select (= select selection)) " primary")
                               (when (= path target-path) " target")
                               (when lane? " lane")
                               (when (= :palette kind) " palette")
                               (when (= :ghost kind) " ghost")
                               (when (= path over-path)
                                 (case where
                                   :front " drop-front"
                                   :back  " drop-back"
                                   (if (= :instance node-kind) " drop-into" " drop-group"))))
                   :style {:padding-left (str (+ 4 (* 11 depth)) "px")}
                   :title label
                   :ref (when (and select (= select selection)) (reveal selection))
                   ;; Labels, bars and stage objects all select the same row.
                   ;; The primary row plus playhead resolves creation.
                   :on-click (fn [^js e]
                               (when select
                                 (choose-row! e select)))
                   ;; An instance's row opens the symbol it places, as a tab.
                   :on-double-click (fn [^js e]
                                      (when of
                                        (rf/dispatch [::pb/open-symbol of])))}
            ;; A node's row can be dragged onto another: onto an instance's, to
            ;; go inside the symbol it places; onto any other node's, to be
            ;; grouped with it into a new one; onto an edge of either, to be
            ;; restacked in front of it or behind it, in whichever symbol it is
            ;; in. All of them keep the picture as it is, but for the stacking.
            node? (merge {:draggable (not editing?)
                          :on-drag-start (fn [^js e]
                                           (.stopPropagation e)
                                           (.setData (.-dataTransfer e) "text/plain" "row")
                                           (set! (.. e -dataTransfer -effectAllowed) "move")
                                           (drag/row! path node-kind select
                                                      (when selected? @(rf/subscribe [::sub/selections])) (first span)))
                          :on-drag-end (fn [_] (reset! over nil) (drag/done!))
                          :on-drag-enter (fn [^js e] (when (takes? path node-kind) (.preventDefault e)))
                          :on-drag-over (fn [^js e]
                                          (when (takes? path node-kind)
                                            (.preventDefault e)
                                            (.stopPropagation e)
                                            (set! (.. e -dataTransfer -dropEffect) "move")
                                            (let [o [path (zone e node-kind)]]
                                              (when (not= o @over) (reset! over o)))))
                          :on-drop (fn [^js e]
                                     (.preventDefault e)
                                     (.stopPropagation e)
                                     (let [from  (when (takes? path node-kind) (drag/row))
                                           where (zone e node-kind)
                                           many (drag/row-selections)]
                                       (reset! over nil)
                                       (drag/done!)
                                       (when from
                                         (rf/dispatch
                                          (cond
                                            (not= :into where) (if (< 1 (count many))
                                                                  [::ui/restack-nodes many path (= :front where)]
                                                                  [::ui/restack from path (= :front where)])
                                            (= :instance node-kind) (if (< 1 (count many))
                                                                    [::ui/move-nodes many path]
                                                                    [::ui/move-node from path])
                                            :else [::ui/group (vec (distinct (concat (if (< 1 (count many)) (map #(nth % 3) many) [from]) [path])))])))))}))
     [:button.tl-twist
      {:disabled (not expandable?)
       ;; This button lives inside a draggable row label. Do not let a tiny
       ;; pointer movement turn disclosure into a native row drag (whose click
       ;; is then suppressed by the browser).
       :draggable false
       :on-pointer-down (fn [^js e] (.stopPropagation e))
       :on-double-click (fn [^js e] (.stopPropagation e))
       :on-click (fn [^js e]
                   (.stopPropagation e)
                   (rf/dispatch [::ui/set-row-expanded path (not expanded?)]))}
      (when expandable? (if expanded? "▾" "▸"))]
     ;; A LANE IS NOT AN INSTANCE WEARING A DIFFERENT HAT, to read. Its one row
     ;; holds blocks that follow one another in time, where every other row
     ;; holds a bar that is simply on screen — so it gets the strip glyph and
     ;; its own ground, and the kind tag below says "lane" rather than naming
     ;; the placement mechanism that happens to carry it.
     (when lane? [:span.tl-ico {:aria-hidden true} [icon/view :lane]])
     (if editing?
       [:input.tl-name-input
        {:value @draft :auto-focus true :aria-label "name"
         :on-click #(.stopPropagation %)
         :on-double-click #(.stopPropagation %)
         :on-change #(reset! draft (.. % -target -value))
         :on-blur commit-rename!
         :on-key-down (fn [^js e]
                        (case (.-key e)
                          "Enter" (do (.preventDefault e) (commit-rename!))
                          "Escape" (do (.preventDefault e) (reset! renaming nil))
                          nil))}]
       [:span.name label])
     ;; How many places show this same drawing: rename it here and every one
     ;; of them changes, and `make unique` is how to stop that.
     (when (and uses (< 1 uses) (not editing?))
       [:span.uses {:title (str "placed " uses " times · a rename reaches every one")}
        (str "(" uses ")")])
     (cond
       named-lane? [:span.kind "·lane"]
       node?  [:span.kind (if via (str "· in " via) (str "·" (name node-kind)))])
     ;; The row's buttons sit together at its right end. Two quick clicks on
     ;; one of them are two clicks, not a double-click that opens the row.
     [:span.tl-actions {:on-double-click (fn [^js e] (.stopPropagation e))}
      (when (and renamable? (not editing?))
        [:button.tl-rename
         {:title (if (= :instance node-kind) "rename symbol · every placement follows" "rename")
          :on-click (fn [^js e] (.stopPropagation e) (begin-rename!))}
         "✎"])
      ;; A tracing layer's row switches it on and off, next to solo because the
      ;; two are the same kind of thing: what this row shows, here, now, and
      ;; nothing the picture keeps. A face's row switches its footage — its
      ;; plate — which is the same switch wherever the face is placed.
      (when-let [layer (cond (contains? (:traces tracing) of) [(second select) (nth select 2)]
                             (contains? (:plated tracing) of) [of :plate])]
        (let [{:keys [on? hidden]} (:state tracing)
              shown? (and on? (not (contains? hidden layer)))]
          [:button {:class    (str "tl-trace" (when shown? " on"))
                    :title    (if (contains? (:traces tracing) of)
                                "show this tracing layer · never exported"
                                "show the footage this face was traced from")
                    :on-click (fn [^js e]
                                (.stopPropagation e)
                                (rf/dispatch [::ui/show-trace layer (not shown?)]))}
           "T"]))
      (when (= :instance node-kind)
        [:button {:class    (str "tl-solo" (when (contains? solo path) " on"))
                  :title    "show only this on the stage (⇧ for more than one)"
                  :on-click (fn [^js e]
                              (.stopPropagation e)
                              (rf/dispatch [::ui/solo path (.-shiftKey e)]))}
         "S"])
      (when (and node? (= select selection))
        [:button.tl-delete {:title    "delete, with everything in it (⌫)"
                            :on-click (fn [^js e]
                                        (.stopPropagation e)
                                        (rf/dispatch [::ui/delete-selected]))}
         "×"])]]))

(defn- paint-key-selection!
  "Selection and preview are transient DOM state. Update them in one pass,
  without scheduling a React render for every marker on every pointer move."
  [elements {:keys [ids positions]}]
  (let [widths (js/Map.)]
    (.forEach elements
      (fn [el]
        (let [items (aget el "arthurKeys")
              selected? (boolean (some ids (aget el "arthurKeyIds")))
              item (first items)
              moved (when selected? (get positions (aget el "arthurKeyId")))]
          (.toggle (.-classList el) "selected" selected?)
          (if moved
            (let [row (.-parentElement el)
                  width (if (.has widths row) (.get widths row)
                            (let [w (.-width (.getBoundingClientRect row))]
                              (.set widths row w) w))
                  dx (* width (/ (- (:at moved) (:at item)) (aget el "arthurFrames")))]
              (set! (.. el -style -transform) (str "translateX(" dx "px)")))
            (set! (.. el -style -transform) "")))))))

(defn- key-dot [items frames chosen preview anchor key-state key-elements]
  (r/with-let [gesture (atom nil) element (atom nil)]
    (let [{:keys [ids]} @key-state
          selected? (some ids (map keyframes/identity-of items))]
      [:button.tl-key
       {:type "button" :class (when selected? "selected")
        :title "Drag to move · Shift-click for a range · Command/Ctrl-click to toggle · Command/Ctrl-A selects all visible keys · arrows to shift · Delete to remove"
        :style {:left (at% (:at (first items)) frames)}
        :ref (fn [el]
               (when @element (.delete key-elements @element))
               (reset! element el)
               (when el
                 (aset el "arthurKeys" items) (aset el "arthurFrames" frames)
                 (aset el "arthurKeyIds" (mapv keyframes/identity-of items))
                 (aset el "arthurKeyId" (keyframes/identity-of (first items)))
                 (.add key-elements el)
                 (paint-key-selection! (js/Set. #js [el]) @key-state)))
        :on-click #(.stopPropagation %)
        :on-double-click #(.stopPropagation %)
        :on-key-down
        (fn [e]
          (let [k (.-key e)]
            (when (#{"Delete" "Backspace" "ArrowLeft" "ArrowRight" "Escape"} k)
              (.preventDefault e) (.stopPropagation e)
              (cond
                (= k "Escape") (reset! chosen [])
                (#{"Delete" "Backspace"} k)
                (do (.focus (.closest (.-currentTarget e) "section.time") #js {:preventScroll true})
                    (rf/dispatch [::ui/edit-keyframes @chosen nil]) (reset! chosen []))
                :else (let [df (* (if (= k "ArrowLeft") -1 1) (if (.-shiftKey e) 10 1))
                            xs @chosen]
                        (rf/dispatch [::ui/edit-keyframes xs df])
                        (reset! chosen (keyframes/shifted xs df)))))))
        :on-pointer-down
        (fn [e]
          (.stopPropagation e)
          (.focus (.-currentTarget e) #js {:preventScroll true})
          (let [ids (set (map keyframes/identity-of items))
                selected? (some (:ids @key-state) (map keyframes/identity-of items))]
            (cond
              (or (.-metaKey e) (.-ctrlKey e))
              (reset! chosen (if selected?
                               (vec (remove #(contains? ids (keyframes/identity-of %)) @chosen))
                               (into @chosen items)))
              (and (.-shiftKey e) @anchor)
              (let [buttons (vec (array-seq (.querySelectorAll (.closest (.-currentTarget e) ".tl-tracks") "button.tl-key")))
                    origin (some #(when (some #{(:id @anchor)} (map keyframes/identity-of (aget % "arthurKeys"))) %) buttons)
                    boxes (js/Map.)
                    box (.getBoundingClientRect (.-currentTarget e))
                    start (when origin (.getBoundingClientRect origin))]
                (when start
                  (reset! chosen
                          (vec (distinct (mapcat #(aget % "arthurKeys")
                            (filter (fn [el]
                                      (let [row (.-parentElement el)
                                            b (if (.has boxes row) (.get boxes row)
                                                  (let [b (.getBoundingClientRect row)] (.set boxes row b) b))
                                            x (- (+ (.-left b) (* (.-width b) (/ (+ (:at (first (aget el "arthurKeys"))) 0.5) frames))) 5.5)
                                            y (- (+ (.-top b) (/ (.-clientHeight row) 2)) 5.5)]
                                        (and (<= (- (min (.-left start) (.-left box)) 0.1) x (+ (max (.-left start) (.-left box)) 0.1))
                                             (<= (- (min (.-top start) (.-top box)) 0.1) y (+ (max (.-top start) (.-top box)) 0.1))))) buttons)))))))
              (not selected?) (reset! chosen items))
            (when-not (.-shiftKey e)
              (reset! anchor {:id (keyframes/identity-of (first items))})))
          (reset! gesture {:x (.-clientX e) :items @chosen
                           :width (.-width (.getBoundingClientRect (.-parentElement (.-currentTarget e))))})
          (try (.setPointerCapture (.-currentTarget e) (.-pointerId e)) (catch :default _ nil)))
        :on-pointer-move
        (fn [e]
          (.stopPropagation e)
          (when @gesture
            (let [df (js/Math.round (* frames (/ (- (.-clientX e) (:x @gesture)) (max 1 (:width @gesture)))))]
              (when (not= df (:delta @gesture))
                (swap! gesture assoc :delta df)
                (reset! preview {:delta df})))))
        :on-pointer-up
        (fn [e]
          (.stopPropagation e)
          (when-let [g @gesture]
            (when (and (seq (:items g)) (not (zero? (or (:delta g) 0))))
              (rf/dispatch-sync [::ui/edit-keyframes (:items g) (:delta g)])
              (reset! chosen (keyframes/shifted (:items g) (:delta g)))))
          (reset! gesture nil) (when @preview (reset! preview nil)))
        :on-pointer-cancel (fn [e] (.stopPropagation e) (reset! gesture nil) (reset! preview nil))}])))

(defn- runout
  "The stretches of a bar drawn from `in` to `out` where what it places has
  nothing to show — see `shows` — as Flash draws empty frames: unfilled, inside
  the outline of a clip that still lasts there."
  [in out shows]
  (when-let [[from to] shows]
    (for [[a b] [[in (min out from)] [(max in to) out]]
          :when (< a b)]
      ^{:key a}
      [:span.tl-runout {:style {:left  (str (* 100 (/ (- a in) (- out in))) "%")
                                :width (str (* 100 (/ (- b a) (- out in))) "%")}}])))

(defn- track-cell
  "`sliding` is the gesture in flight: `{:row :path :kind :from :df}`, where
  `:from` is the frame the press was on and `:df` how many frames the pointer has
  moved since. What it looks like mid-drag is `[:ui :sliding]`, which the clip
  every row and the stage are drawn from already has in it."
  [{:keys [path span shows keys key-items gaps dense? kind node-kind select slides cels lane? of unmapped? owner]}
   frames sliding hint {:keys [clip store open selection selections target-path key-selection key-preview key-anchor key-state key-elements choose-row!]}]
  (let [selected-set (set selections)
        active-row (:row @sliding)
        slide   (fn [^js e]
                  (let [{from :row pressed :from} @sliding]
                    ;; Pointer capture already makes this the one track receiving
                    ;; the gesture. Do not compare its presentation path: the
                    ;; palette lane is a view of another symbol and its synthetic
                    ;; row address is not the authored clip path.
                    (when from
                      ;; HOW MANY FRAMES THE POINTER HAS MOVED, as the difference
                      ;; of two frames under it -- not a pixel distance scaled and
                      ;; rounded by itself. The ghost below and the command on
                      ;; release are handed this same number, so what the drag
                      ;; shows is what the drag does.
                      (let [df (- (frame-under e frames (.-currentTarget e)) pressed)
                            drag (:drag @sliding)
                            ;; TWO INTENTIONS, SAID BY A MODIFIER. An ordinary
                            ;; body drag moves a clip in TIME, within its lane
                            ;; or into another. Holding shift means something
                            ;; else entirely: put this node INSIDE the symbol
                            ;; the clip under the pointer places, keeping where
                            ;; it looks and when it happens. Overlap cannot say
                            ;; which is meant — dropping on occupied time
                            ;; already means claiming it — so the person says.
                            shift? (.-shiftKey e)
                            under (when (and drag shift?) (clip-under e))
                            ;; Asked of the command itself, so the hint cannot
                            ;; promise what the drop would refuse.
                            ;; THE PLAYHEAD, READ HERE AND NOT TAKEN AS A
                            ;; PROP. As a prop it was a new value on every tick,
                            ;; so every cell in the pane re-rendered throughout
                            ;; playback for a number that only a shift-drag ever
                            ;; reads. This runs from a pointer handler rather
                            ;; than a render, so the deref takes no dependency
                            ;; on it, and on this rare a path an uncached sub
                            ;; costs nothing.
                            why   (when (and under (not= (:select under) (:selection drag)))
                                    (if (seq (:paths @sliding))
                                      (:refused (clipboard/move-many clip store open selections
                                                                      (nth (:select under) 3)
                                                                      @(rf/subscribe [::render/open-frame]) 0))
                                      (nest/move-refusal clip store open
                                                         (nth (:selection drag) 3)
                                                         (nth (:select under) 3)
                                                         @(rf/subscribe [::render/open-frame]))))
                            nest  (when (and under (nil? why)
                                             (not= (:select under) (:selection drag)))
                                    under)
                            [target-el target] (when (and drag (not shift?)) (lane-under e))
                            target-at (when target
                                        (frame-under e frames target-el))
                            ;; A cel moved on its OWN lane is a normal slide.
                            ;; Resolve the row under the pointer before calling
                            ;; it a transfer: comparing the displayed rows is
                            ;; not enough when the lane is reached through an
                            ;; instance.  This leaves one slide gesture and one
                            ;; coordinate conversion; lane overlap trimming is
                            ;; the domain command's only additional policy.
                            target-sid (when target
                                         (:sid (ui/drop-destination-at
                                                clip store open target-at target)))
                            ;; Row identity answers before document addressing:
                            ;; remaining on the DOM track that captured the
                            ;; pointer is a slide. This is true for every lane
                            ;; and avoids asking special rows (such as the
                            ;; palette track) to counterfeit an ordinary target
                            ;; address merely to recognize themselves.
                            landing? (and target-sid
                                          (not (identical? target-el (.-currentTarget e)))
                                          (not= target-sid (nth (:selection drag) 1)))
                            target-frame (when landing?
                                           (max 0 (- target-at (:grab drag))))]
                        (when (and drag hint)
                          (reset! hint
                                  {:x (.-clientX e) :y (.-clientY e)
                                   :nest? (boolean nest)
                                   :no? (boolean why)
                                   :text (cond
                                           nest (str "nest into " (:label nest))
                                           why (str "can't nest here · " why)
                                           shift? "shift: nest into a clip"
                                           :else "drop to place · shift to nest")}))
                        (cond
                          nest
                          (do
                            (swap! sliding #(-> % (assoc :nest nest)
                                                (dissoc :target-lane :target-frame)))
                            (rf/dispatch [::ui/sliding nil]))
                          landing?
                          (do
                            (swap! sliding #(-> % (assoc :target-lane target
                                                         :target-frame target-frame)
                                                (dissoc :nest)))
                            (rf/dispatch [::ui/sliding nil]))
                          :else
                          (do
                            (swap! sliding dissoc :target-lane :target-frame :nest)
                            (when (not= df (:df @sliding))
                              (swap! sliding assoc :df df)
                              (rf/dispatch [::ui/sliding (:path @sliding) df
                                            (:kind @sliding) (:ripple? @sliding)
                                            (:other @sliding) owner (:paths @sliding)]))))))))
        done    (fn [commit?]
                  (when @sliding
                    (let [{:keys [path df kind ripple? other target-lane target-frame
                                  drag nest on-click more? range? paths]} @sliding]
                      (reset! sliding nil)
                      (when hint (reset! hint nil))
                      (cond
                        (and commit? nest drag)
                        (do (rf/dispatch [::ui/sliding nil])
                            ;; `nest/move-node`, which is what keeps the world
                            ;; transform and the root timing across the move.
                            (rf/dispatch (if (seq paths)
                                           [::ui/move-nodes selections (nth (:select nest) 3)]
                                           [::ui/move-node (nth (:selection drag) 3)
                                            (nth (:select nest) 3)])))
                        (and commit? target-lane drag)
                        (do (rf/dispatch [::ui/sliding nil])
                            (rf/dispatch (if (seq paths)
                                           [::ui/move-nodes selections (nth target-lane 3)
                                            target-frame (- target-frame (:in drag))]
                                           [::ui/drop-clip (:selection drag)
                                            target-lane target-frame])))
                        commit?
                        ;; A press that moved nothing is a click, and a drag
                        ;; that only moved in time leaves what it moved
                        ;; selected. The two structural cases above select what
                        ;; they landed, so neither needs this.
                        (do (when (and on-click (or (zero? df) (not (seq paths))))
                              (choose-row! #js {:metaKey more? :shiftKey range?} on-click))
                            (rf/dispatch [::ui/slide path df kind ripple? other owner paths]))
                        :else (rf/dispatch [::ui/sliding nil])))))
        begin!  (fn [^js e actual-path gesture-kind actual-select other drag]
                  (let [track (.closest (.-currentTarget e) ".tl-track")]
                    (.stopPropagation e)
                    (.focus track #js {:preventScroll true})
                    ;; SELECTING WAITS FOR THE RELEASE. Selecting on the press
                    ;; changed what the timeline was showing before the gesture
                    ;; had said anything: an expanded lane follows the
                    ;; selection, so pressing a clip to drag it somewhere shut
                    ;; the portal holding the lane being dragged INTO, out from
                    ;; under the pointer. The press now only starts the
                    ;; gesture; what it meant is known on release.
                    (reset! sliding {:row path :path actual-path :kind gesture-kind
                                     :other other
                                     :paths (when (and (#{:slide :in :out} gesture-kind) (not owner)
                                                       (contains? selected-set actual-select)
                                                       (< 1 (count selections)))
                                              (mapv #(nth % 3) (filter #(= :node (first %)) selections)))
                                     :on-click actual-select
                                     :more? (or (.-metaKey e) (.-ctrlKey e))
                                     :range? (.-shiftKey e)
                                     :drag (when drag
                                             (assoc drag
                                                    :grab (- (frame-under e frames track)
                                                             (:in drag))))
                                     :ripple? (and (= :out gesture-kind) (.-shiftKey e))
                                     ;; The frame pressed, which every later
                                     ;; answer is measured from. No pixel origin
                                     ;; and no track width: the box is measured
                                     ;; when it is asked, so a pane resized or
                                     ;; zoomed mid-drag cannot skew the gesture.
                                     :from (frame-under e frames track) :df 0})
                    (try (.setPointerCapture track (.-pointerId e))
                         (catch :default _ nil))))]
    [:div.tl-track
     ;; The track, not the bar, holds the pointer while a bar slides, so the drag
     ;; goes on when the bar has slid off the ruler and is no longer drawn.
     {:class (str (when lane? "lane") (when (= :palette kind) " palette")
                  (when (= :channel kind) " automation")
                  (when (= path target-path) " target"))
      :tab-index 0
      :on-key-down (fn [e]
                     (when (and (empty? @key-selection) (= (.-target e) (.-currentTarget e))
                                (#{"ArrowLeft" "ArrowRight"} (.-key e)))
                       (.preventDefault e) (.stopPropagation e)
                       (let [paths (mapv #(nth % 3) (filter #(= :node (first %)) selections))
                             df (* (if (= "ArrowLeft" (.-key e)) -1 1)
                                   (if (.-shiftKey e) 10 1))]
                         (when (seq paths)
                           (rf/dispatch [::ui/slide (first paths) df :slide false nil nil paths])))))
      :on-pointer-move   slide
      :on-pointer-up     (fn [e] (slide e) (done true))
      :on-pointer-cancel (fn [_] (done false))
      ;; THE CLICK ARRIVES HERE, not on the block it started on, because the
      ;; track captured the pointer. Opening what was double-clicked is
      ;; therefore the track's job: the block under the pointer if there is
      ;; one, else this row's own instance. It opens as a tab, which is what
      ;; double-clicking the same symbol in the pool does.
      :on-double-click (fn [^js e]
                         (let [under (clip-under e)]
                           (cond
                             (and lane? (nil? under))
                             (do (.stopPropagation e)
                                 (rf/dispatch [::ui/new-symbol-at
                                               (frame-at e frames) select]))

                             :else
                             (when-let [source (or (:source under) of)]
                               (.stopPropagation e)
                               (rf/dispatch [::pb/open-symbol source])))))
      :ref (when lane? (fn [el] (when el (aset el "arthurLane" select))))
      :on-drag-enter (fn [^js e]
                       (cond
                         (and (not= :palette kind) lane?
                              (or (drag/accepts?) (drag/row)))
                         (do (.preventDefault e) (.stopPropagation e))))
      :on-drag-over (fn [^js e]
                      (cond
                        (and (not= :palette kind) lane?
                             (or (drag/accepts?) (drag/row)))
                        (do (.preventDefault e) (.stopPropagation e)
                            (set! (.. e -dataTransfer -dropEffect)
                                  (if (drag/row) "move" "copy"))
                            (when (drag/accepts?)
                              (drag/hover! :timeline (frame-at e frames) nil select)))))
      :on-drop (fn [^js e]
                 (cond
                   (and (not= :palette kind) lane?
                        (or (drag/accepts?) (drag/row)))
                   (do (.preventDefault e)
                       (.stopPropagation e)
                       (if-let [from (drag/row-selection)]
                         (let [many (drag/row-selections)
                               in (drag/row-in)
                               at (frame-at e frames)]
                           (drag/done!)
                           (rf/dispatch (if (and (< 1 (count many)) (number? in))
                                          [::ui/move-nodes many (nth select 3) at (- at in)]
                                          [::ui/drop-clip from select at])))
                         (drag/land! (frame-at e frames) nil select)))))}
     ;; Clipped to the ruler: an instance longer than the room left in its
     ;; symbol still plays its own frames from 0, it is just cut off at the end.
     (when-let [[in out] (when (and span (nil? cels)) [(max 0 (first span)) (min frames (second span))])]
       (when (< in out)
         ;; AN UNMAPPED BAR IS NOT DRAGGABLE. Inside a held clip a nested row
         ;; is shown across the whole hold because that is when it is on
         ;; screen, not because its frames are this ruler's: there is no
         ;; mapping to edit through, so the bar selects and does not slide.
         [:div {:class (str "tl-span" (when dense? " dense") (when (= :ghost kind) " ghost")
                            (when (= :audio node-kind) " sound")
                            (when (= :trace (get-in clip [:symbols of :type])) " trace")
                            (when unmapped? " unmapped")
                            (when (and select (not unmapped?)) " movable")
                            (when (and select (contains? selected-set select)) " on")
                            (when (and select (= select selection)) " primary")
                            (when (= path active-row) " sliding"))
                :title (when unmapped?
                         "inside a held clip · its own frames have no place on this ruler")
                :style {:left (edge% in frames)
                        :width (str (* 100 (/ (- out in) (max 1 frames))) "%")}
                :on-click (when (and select unmapped?)
                            (fn [^js e] (.stopPropagation e)
                              (choose-row! e select)))
                :ref (when (and select (not unmapped?))
                       (fn [^js el] (when el (aset el "arthurSelect" select))))
                :on-pointer-down
                (when (and select (not unmapped?))
                  #(begin! % (or slides path) :slide select nil nil))}
          (runout in out shows)
          (when (and select (not unmapped?))
            [:span.tl-edge.in {:title "Drag start"
                               :on-pointer-down #(begin! % path :in select nil nil)}])
          (when (and select (not unmapped?))
            [:span.tl-edge.out {:title "Drag endpoint · Shift-drag ripples later clips"
                                :on-pointer-down #(begin! % path :out select nil nil)}])]))
     (doall
      (for [[i {:keys [id label source span shows select ghost?]}]
            (map-indexed vector
                         (cond-> (vec cels)
                           (= select (:target-lane @sliding))
                           (conj {:id ::moving :label (get-in @sliding [:drag :label])
                                  :span [(:target-frame @sliding)
                                         (+ (:target-frame @sliding)
                                            (get-in @sliding [:drag :duration]))]
                                  :ghost? true})))
            :let [prev (when (pos? i) (nth cels (dec i)))
                  joined? (and (not ghost?) prev (= (second (:span prev)) (first span)))
                  in (max 0 (first span)) out (min frames (second span))]
            :when (< in out)]
        ^{:key (str id)}
        [:button.tl-cel
         {:title (if (= :palette kind)
                   (str label " · select palette transition")
                   (str label " · select clip; double-click to edit its symbol"
                        " · shift-drag another clip onto it to nest that clip inside"))
          ;; WHICH CLIP IS SELECTED IS THE QUESTION THIS ROW ANSWERS. An
          ;; expanded lane opens exactly the selected clip, so a block that
          ;; cannot say whether it is the one leaves the portal below it
          ;; unexplained. It wears the pick colour rather than the accent every
          ;; span and drop already uses — see `--pick` in app.css.
          :class (str (when ghost? "ghost")
                      ;; A reference, not the picture: drawn so it cannot be
                      ;; mistaken for a drawing that will be exported.
                      (when (= :trace (get-in clip [:symbols source :type])) " trace")
                      (when (and select (contains? selected-set select)) " on")
                      (when (and select (= select selection)) " primary")
                      (when (= (nth select 3 nil) target-path) " target")
                      (when (and select (= select (get-in @sliding [:nest :select])))
                        " nest-target"))
          :style {:position "absolute" :left (edge% in frames)
                  :width (str (* 100 (/ (- out in) (max 1 frames))) "%")
                  :top "2px" :bottom "2px" :overflow "visible" :padding "0 3px"}
          ;; What this block is, for the track to read back: the click that
          ;; selects it and the double-click that opens it are both delivered
          ;; to the track, which holds the pointer. See `clip-under`.
          :ref (when select
                 (fn [^js el]
                   (when el (aset el "arthurCel" {:select select :source source
                                                  :label label
                                                  :in (js/Math.floor in)}))))
          ;; `:in` IS A RULER FRAME AND THEREFORE WHOLE, as the ref above
          ;; already says. `:grab` is the pointer's offset into the block,
          ;; `press − in`, and `target-frame` is `move − grab`, so a fractional
          ;; `in` would make every landing frame fractional. A clip of this
          ;; symbol now starts on a whole frame of this ruler by construction --
          ;; they are the same frame space -- and the floor is what still holds
          ;; for a row reached through a retimed instance, where the clip really
          ;; does begin between two of the ruler's frames.
          :on-pointer-down (when select
                             #(begin! % (nth select 3) :slide select nil
                                      {:selection select :label label
                                       :in (js/Math.floor in)
                                       :duration (- out in)}))}
         (runout in out shows)
         [:span.tl-cel-label label]
         (when select
           (if joined?
             ;; ONE HANDLE, ONE MEANING: the cut moves. The end of the clip on the
             ;; left and the start of the one on the right are the same frame, so
             ;; a gesture on that frame alters the extent of one and then the
             ;; other -- `span/roll`, which is literally `resize-out` then
             ;; `resize-in`.
             ;;
             ;; It used to pick between three things by where in the handle the
             ;; press landed: trim-left, roll, trim-right, in thirds of twelve
             ;; pixels. Four pixels is not an aim, so a gesture that meant to roll
             ;; trimmed one side instead and the handle looked like it chose for
             ;; itself. Trimming one side alone leaves a GAP, which is a different
             ;; intention and has its own handle on an edge that is not shared.
             [:span.tl-junction
              {:title "Drag the cut · the clip before it ends here and the one after it starts here"
               :on-pointer-down
               ;; Between two clips of a selection, it is the end of the one
               ;; before, and every selected end moves with it.
               (fn [^js e]
                 (if (and (< 1 (count selections))
                          (contains? selected-set select)
                          (contains? selected-set (:select prev)))
                   (begin! e (nth (:select prev) 3) :out (:select prev) nil nil)
                   (begin! e (nth select 3) :roll select (nth (:select prev) 3) nil)))}]
             [:span.tl-edge.in {:title "Drag start"
                                :on-pointer-down #(begin! % (nth select 3) :in select nil nil)}]))
         (when select
           [:span.tl-edge.out {:title "Drag endpoint · Shift-drag ripples later clips"
                               :on-pointer-down #(begin! % (nth select 3) :out select nil nil)}])]))
     ;; Click a gap between two keys to switch it between holding and tweening.
     (doall
      (for [{:keys [sid id channel left from to tween?]} gaps
            :when (and (< from frames) (< 0 to))]
        ^{:key left}
        [:button.tl-gap
         {:type "button" :class (when tween? "tween")
          :title (if tween? "tween · click to hold" "hold · click to tween")
          :style {:left (at% from frames)
                  :width (str (* 100 (/ (- to from) (max 1 frames))) "%")}
          :on-pointer-down #(.stopPropagation %)
          :on-click (fn [^js e]
                      (.stopPropagation e)
                      (rf/dispatch [::project/set-segment-interp sid id channel left
                                    (if tween? :hold :linear)]))}]))
     ;; A dense channel has a value on every frame, so ticking each one is a solid
     ;; block that says less than the bar behind it already does.
     ;; The lane's own keys, and those of the clips on it drawn after the
     ;; blocks so they land ON the block they belong to: a collapsed lane still
     ;; says where the thing in it changes, without opening anything.
     (when (or (not dense?) (seq key-items) (some (comp seq :key-items) cels))
       (doall
        (let [by-frame (group-by :at (concat key-items (mapcat :key-items cels)))]
         (for [[index f] (map-indexed vector (sort (distinct (concat keys (mapcat :keys cels)))))
              :when (and (<= 0 f) (< f frames))]
          (if-let [items (and (= :channel kind) (seq (get by-frame f)))]
            ^{:key index} [key-dot (vec items) frames key-selection key-preview key-anchor key-state key-elements]
            ^{:key index} [:div.tl-key {:aria-hidden true :style {:left (at% f frames)}}])))))]))

(defn- cursor-hint
  "What the drag in flight would do, beside the pointer.

  ITS OWN COMPONENT, deref'ing its own atom: the pointer moves many times a
  second and every row of the timeline reads `sliding`, so putting the pointer
  position in there would repaint the whole pane to move a label two pixels."
  [hint]
  (when-let [{:keys [x y text nest? no?]} @hint]
    [:div.tl-hint {:class (str (when nest? "nesting") (when no? "refusing"))
                   :style {:left (str (+ x 16) "px") :top (str (+ y 18) "px")}}
     text]))

(defn- playhead
  "The knob on the ruler and the line down the tracks, as their own component.

  THE PLAYHEAD IS THE ONLY THING IN THIS PANE THAT MOVES DURING PLAYBACK, so it
  derefs the frame ITSELF and is the only thing the frame moving re-renders.
  Read in `timeline-view`'s body instead — which is where it was — `::pb/tick`
  invalidated the whole pane every painted frame: `rows`, `sound-rows` and the
  symbol ordering recomputed from scratch and every track cell re-rendered, all
  to move two elements' `left`. That cost more than painting the frame did and
  starved the rAF loop painting it, which ran at 16fps against a 60fps ceiling."
  [tag frames]
  (let [f @(rf/subscribe [::render/open-frame])
        head? (= tag :div.tl-knob)]
    ;; The head is as wide as its number needs and never narrower than the frame.
    [tag {:style {:left (edge% f frames) (if head? :min-width :width) (edge% 1 frames)}}
     (when head? f)]))

(defn- outside-marks
  "The frames playback leaves out, shaded, and the span it plays, banded on the
  ruler. Its own component, deref'ing the marks itself, as `playhead` does."
  [frames ruler?]
  (when-let [{:keys [in out]} @(rf/subscribe [::playback/range])]
    (let [lo (or in 0) hi (inc (min (dec frames) (or out (dec frames))))]
      (if ruler?
        [:<>
         [:div.tl-range {:style {:left (edge% lo frames) :width (edge% (- hi lo) frames)}}]
         (for [[which f at] [[:in in lo] [:out out hi]] :when f]
           ^{:key which}
           [:div.tl-mark
            {:class (name which) :style {:left (edge% at frames)}
             :title (str (name which) " mark · drag to move")
             :on-pointer-down (fn [^js e]
                                (.stopPropagation e)
                                (try (.setPointerCapture (.-currentTarget e) (.-pointerId e))
                                     (catch :default _ nil)))
             :on-pointer-move (fn [^js e]
                                (when (.hasPointerCapture (.-currentTarget e) (.-pointerId e))
                                  (rf/dispatch [::pb/drag-mark which
                                                (frame-under e frames (.closest (.-currentTarget e) ".tl-ruler"))])))}])]
        [:<>
         (when (pos? lo) [:div.tl-outside {:style {:left 0 :width (edge% lo frames)}}])
         (when (< hi frames) [:div.tl-outside {:style {:left (edge% hi frames) :right 0}}])]))))

(defn- swept
  "The clips and bars inside `tracks` that the box from `[x y]` to `[x2 y2]`, in
  client pixels, touches, as their selection addresses with their elements."
  [^js tracks x y x2 y2]
  (let [l (min x x2) r (max x x2) t (min y y2) b (max y y2)]
    (keep (fn [^js el]
            (when-let [select (or (some-> (aget el "arthurCel") :select)
                                  (aget el "arthurSelect"))]
              (let [box (.getBoundingClientRect el)]
                (when (and (< (.-left box) r) (< l (.-right box))
                           (< (.-top box) b) (< t (.-bottom box)))
                  [el select]))))
          (array-seq (.querySelectorAll tracks ".tl-cel, .tl-span")))))

(defn- mark-swept!
  "Show what the box holds by a class on the blocks themselves, not by
  selecting: selecting re-lays the pane — an expanded lane follows the
  selection — and the rows would move under the box being drawn over them."
  [^js tracks els]
  (doseq [^js el (array-seq (.querySelectorAll tracks ".swept"))]
    (.remove (.-classList el) "swept"))
  (doseq [^js el els] (.add (.-classList el) "swept")))

(defn- key-marquee [marquee]
  (when-let [{:keys [x y x2 y2]} @marquee]
    (when (and x2 y2)
      [:div.tl-key-marquee {:style {:position "fixed" :pointer-events "none" :z-index 20
                                    :left (str (min x x2) "px") :top (str (min y y2) "px")
                                    :width (str (abs (- x2 x)) "px") :height (str (abs (- y2 y)) "px")
                                    :border "1px solid var(--pick)" :background "color-mix(in srgb, var(--pick) 15%, transparent)"}}])))

(defn- frame-step
  "How many frames apart the grid's fine lines are: every frame where a frame
  is wide enough to see, else the fewest that divide a second evenly and are —
  so a line still falls on every second — or nil when not even that fits under
  the second lines."
  [frame-px fps]
  (first (filter #(and (zero? (mod fps %)) (<= 6 (* % frame-px)) (< % fps))
                 (range 1 fps))))

(defn- timeline-view []
  (r/with-let [scrubbing (r/atom false)
               ;; How wide the tracks are drawn, for how many pixels a frame
               ;; gets: the grid and the playhead change with it.
               tracks-px (r/atom nil)
               sizer (js/ResizeObserver.
                      (fn [entries] (reset! tracks-px (.. (aget entries 0) -contentRect -width))))
               ;; The row a carried row is over and which part of it, for the
               ;; highlight.
               over (r/atom nil)
               sliding (r/atom nil)
               hint (r/atom nil)
               renaming (r/atom nil)
               draft (r/atom "")
               key-selection (atom [])
               key-preview (atom nil)
               key-elements (js/Set.)
               key-state (atom {:ids #{} :positions nil})
               selection-watch
               (add-watch key-selection ::selection
                 (fn [_ _ _ items]
                   (reset! key-state {:ids (into #{} (map keyframes/identity-of) items) :positions nil})
                   (paint-key-selection! key-elements @key-state)))
               preview-watch
               (add-watch key-preview ::preview
                 (fn [_ _ _ preview]
                   (let [items @key-selection delta (or (:delta preview) 0)]
                     (swap! key-state assoc :positions
                            (when-not (zero? delta)
                              (into {} (map vector (map keyframes/identity-of items)
                                            (keyframes/shifted items delta)))))
                     (paint-key-selection! key-elements @key-state))))
               key-context (atom nil)
               key-anchor (atom nil)
               row-anchor (atom nil)
               marquee (r/atom nil)
               ;; A box dragged from empty track space over clips and bars:
               ;; `{:x :y :base}` pressed, then `:x2 :y2 :hits` once it has moved
               ;; far enough to be a drag and not a click. `swept?` swallows the
               ;; click a drag ends in, which would otherwise select nothing.
               sweep (r/atom nil)
               swept? (atom false)]
    (let [clip-id   @(rf/subscribe [::render/clip-id])
          clip      @(rf/subscribe [::render/clip])
          ;; THE RULER IS THE OPEN SYMBOL'S OWN FRAME SPACE. Every span, key and
          ;; cut drawn here is stored in that space, so drawing them against the
          ;; output grid put almost none of them on a mark and left every gesture
          ;; converting and rounding — see `docs/one-grid-plan.md`. The transport
          ;; keeps the output numbers; this pane does not use them.
          frames    (max 1 (or @(rf/subscribe [::render/open-frames]) 1))
          store     @(rf/subscribe [::render/store])
          selection @(rf/subscribe [::sub/selection])
          selections @(rf/subscribe [::sub/selections])
          selection-set (set selections)
          creation  @(rf/subscribe [::sub/creation-target])
          target-path (let [path (:path creation)]
                        (if (and (empty? path)
                                 (= :lane (:kind creation)))
                          [::lane]
                          path))
          expanded  @(rf/subscribe [::sub/expanded])
          drop      @(rf/subscribe [::sub/drop])
          solo      (set @(rf/subscribe [::render/solo]))
          open      @(rf/subscribe [::render/open])
          ;; Which rows offer a tracing switch — placements of a tracing symbol,
          ;; and faces that have footage — and what is switched on. Sets rather
          ;; than a lookup per row.
          tracing   {:traces (into #{} (comp (filter #(= :trace (:type (val %)))) (map key))
                                   (:symbols clip))
                     :plated (into #{} (comp (filter #(get-in (val %) [:nodes :plate])) (map key))
                                   (:symbols clip))
                     :state  @(rf/subscribe [::render/tracing])}
          ;; WHERE THE DROP WILL LAND IS WHERE THE PREVIEW GOES, and what is being
          ;; carried does not come into it. A row under the pointer takes the
          ;; preview as a block in that row -- the same row `ui/drop-destination`
          ;; will resolve on release -- and with no row under the pointer it is a
          ;; row of its own at the top of the section it will appear in.
          ;;
          ;; A SOUND USED TO BE THE EXCEPTION: over a lane it drew no block
          ;; there and a ghost row down in the audio section instead, so dragging
          ;; a sound onto a lane looked exactly like a lane refusing it -- while
          ;; the drop itself worked and put the sound in the lane. One rule
          ;; resolves the drop, so one rule previews it.
          drop-lane (:target drop)
          drop-span (when drop [(:frame drop) (+ (:frame drop) (or (:frames drop) 1))])
          ghost     (when (and drop (nil? drop-lane))
                      {:path [::drop] :depth 0 :kind :ghost
                       :label (str "+ " (:label drop))
                       :span drop-span :keys []})
          lane-ghost (when (and drop drop-lane)
                       {:id ::drop :label (str "+ " (:label drop)) :ghost? true
                        :span drop-span})
          chosen    (when (= :node (first selection)) (nth selection 3))
          picture   (cond-> (cond->> (into (palette-track-rows clip open frames expanded chosen)
                                            (rows clip open expanded chosen))
                              lane-ghost
                              (mapv (fn [row]
                                      (if (= drop-lane (:select row))
                                        (update row :cels (fnil conj []) lane-ghost)
                                        row))))
                      ;; An untargeted visual drop becomes a new ordinary row
                      ;; at the bottom. Inserting it above the lanes shifts the
                      ;; lane under the pointer and makes the browser alternate
                      ;; between this ghost and that lane's drop target.
                      (and ghost (not (:sound? drop)))
                      (conj ghost))
          sounds    (cond->> (into (vec (filter :sound? picture))
                                   (sound-rows clip open expanded))
                      (and ghost (:sound? drop)) (cons ghost))
          picture   (remove :sound? picture)
          ;; The audio section's heading is a row like the others, so the two
          ;; columns stay aligned without measuring anything.
          visible   (cond-> (vec picture)
                      (seq sounds) (-> (conj {:path [::sounds] :kind :section :label "audio"})
                                       (into sounds)))
          ;; Major marks are whole seconds. On long timelines use a whole-number
          ;; multiple of a second to keep roughly ten labels; never invent an
          ;; FPS-blind 20/40/60 ruler.
          fps       (max 1 (or (:fps clip) 1))
          step      (* fps (max 1 (js/Math.ceil (/ frames (* fps 10)))))
          frame-px  (/ (or @tracks-px 0) frames)
          fine      (frame-step frame-px fps)
          choose-row! (fn [e target]
                        (let [xs (vec (keep :select visible))
                              anchor (or @row-anchor selection target)
                              a (.indexOf xs anchor) b (.indexOf xs target)]
                          (cond
                            (or (.-metaKey e) (.-ctrlKey e)) (rf/dispatch [::ui/toggle-selection target])
                            (and (.-shiftKey e) (<= 0 a) (<= 0 b))
                            (rf/dispatch [::ui/select-many (subvec xs (min a b) (inc (max a b)))])
                            :else (rf/dispatch [::ui/select target]))
                          (when-not (.-shiftKey e) (reset! row-anchor target))))]
      (when (not= [clip-id open] @key-context)
        (reset! key-context [clip-id open]) (reset! key-selection []) (reset! key-preview nil) (reset! key-anchor nil) (reset! row-anchor nil))
      [:section.pane.time
       {:aria-label "timeline"
        :tab-index -1
        :on-key-down (fn [e]
                       (when (and (= (.-target e) (.-currentTarget e))
                                  (#{"Delete" "Backspace"} (.-key e)))
                         (.preventDefault e) (.stopPropagation e)))
        :on-pointer-down-capture (fn [e]
                                  (when-not (or (.closest (.-target e) "button.tl-key")
                                                (.closest (.-target e) ".tl-track")
                                                (.-metaKey e) (.-ctrlKey e) (.-shiftKey e))
                                    (reset! key-selection []) (reset! key-preview nil)))}
       [transport]
       [:div.tl-body
        ;; Blank timeline space means the open symbol. Child controls stop their
        ;; own events; the target check keeps ordinary row/bar clicks local.
        {:on-click (fn [^js e]
                     (when (= (.-target e) (.-currentTarget e))
                       (rf/dispatch [::ui/select nil])))
         ;; ON A PHONE ONE FINGER IS THE POINTER AND TWO SCROLL, as on the
         ;; stage: one drags a clip, a box or the playhead, and the rows only
         ;; move under two, by their middle. Properties, not listeners, since
         ;; this runs on every render; on an element, so not passive.
         :ref (fn [^js el]
                (when el
                  (let [middle (fn [^js ts]
                                 (let [a (aget ts 0) b (aget ts 1)]
                                   [(/ (+ (.-clientX a) (.-clientX b)) 2)
                                    (/ (+ (.-clientY a) (.-clientY b)) 2)]))]
                    (set! (.-ontouchstart el)
                          (fn [^js e]
                            (when (<= 2 (.. e -touches -length))
                              (when @sweep
                                (reset! sweep nil)
                                (mark-swept! el []))
                              (aset el "arthurTwo" (middle (.-touches e))))))
                    (set! (.-ontouchmove el)
                          (fn [^js e]
                            (when-let [[x y] (and (<= 2 (.. e -touches -length))
                                                  (aget el "arthurTwo"))]
                              (.preventDefault e)
                              (let [[x2 y2 :as m] (middle (.-touches e))]
                                (set! (.-scrollLeft el) (+ (.-scrollLeft el) (- x x2)))
                                (set! (.-scrollTop el) (+ (.-scrollTop el) (- y y2)))
                                (aset el "arthurTwo" m)))))
                    (set! (.-ontouchend el)
                          (fn [^js e]
                            (when (< (.. e -touches -length) 2)
                              (aset el "arthurTwo" nil)))))))}
        [:div.tl-labels
         ;; Empty label space takes a row back out to the top of the open symbol.
         {:on-click (fn [^js e]
                      (when (= (.-target e) (.-currentTarget e))
                        (rf/dispatch [::ui/select nil])))
          :on-drag-over (fn [^js e]
                          (when (drag/row)
                            (.preventDefault e)
                            (set! (.. e -dataTransfer -dropEffect) "move")))
          :on-drop (fn [^js e]
                     (.preventDefault e)
                     (when-let [from (drag/row)]
                       (let [many (drag/row-selections)]
                         (drag/done!)
                         (reset! over nil)
                         (when (< 1 (count from))
                           (rf/dispatch (if (< 1 (count many)) [::ui/move-nodes many []] [::ui/move-node from []]))))))}
         [:div.tl-corner]
         (doall (for [row visible]
                  (with-meta (if (= :section (:kind row))
                               [:div.tl-label.tl-section (:label row)]
                               [label-cell row selection selection-set target-path
                                over solo tracing renaming draft choose-row!])
                    {:key (str (:path row))})))]
        [:div.tl-tracks
         {:on-pointer-down-capture
          (fn [e]
            ;; DRAG TO SELECT, FROM EMPTY SPACE: anywhere a press would
            ;; otherwise do nothing. Not yet a box — a press that does not move
            ;; is still a click, and a double-click in a lane still makes a
            ;; drawing — so nothing is stopped until it moves.
            (reset! swept? false)
            (when (and (= 0 (.-button e)) (.-isPrimary e)
                       (not (.closest (.-target e) ".tl-track.automation"))
                       (not (.closest (.-target e) "button, .tl-span, .tl-edge, .tl-junction, .tl-ruler, input")))
              (reset! sweep {:x (.-clientX e) :y (.-clientY e)
                             :base (if (or (.-metaKey e) (.-ctrlKey e) (.-shiftKey e)) selections [])}))
            (when (and (= 0 (.-button e))
                       (.closest (.-target e) ".tl-track.automation")
                       (not (.closest (.-target e) "button, .movable, .unmapped, .tl-edge")))
              (.stopPropagation e) (.preventDefault e)
              (.focus (.-currentTarget e) #js {:preventScroll true})
              (reset! marquee {:x (.-clientX e) :y (.-clientY e)
                                 :hits (let [container (.-currentTarget e)]
                                        (vec (mapcat
                                          (fn [row]
                                            (let [b (.getBoundingClientRect row)]
                                              (map (fn [el]
                                                     (let [items (aget el "arthurKeys")]
                                                       {:x (+ (.-left b) (* (.-width b) (/ (+ (:at (first items)) 0.5) frames)))
                                                        :y (+ (.-top b) (/ (.-height b) 2)) :items items}))
                                                   (array-seq (.querySelectorAll row "button.tl-key")))))
                                          (array-seq (.querySelectorAll container ".tl-track")))))
                                :base (if (or (.-metaKey e) (.-ctrlKey e) (.-shiftKey e)) @key-selection [])})
              (reset! key-selection (:base @marquee))
              (try (.setPointerCapture (.-currentTarget e) (.-pointerId e)) (catch :default _ nil))))
          :on-pointer-move
          (fn [e]
            (when-let [{:keys [x y x2]} @sweep]
              (let [cx (.-clientX e) cy (.-clientY e) tracks (.-currentTarget e)]
                ;; Four pixels, as a drag begins anywhere else in the app.
                (when (or x2 (< 4 (js/Math.hypot (- cx x) (- cy y))))
                  (.stopPropagation e)
                  (when-not x2
                    (try (.setPointerCapture tracks (.-pointerId e)) (catch :default _ nil)))
                  (let [hits (swept tracks x y cx cy)]
                    (mark-swept! tracks (map first hits))
                    (swap! sweep assoc :x2 cx :y2 cy :hits (mapv second hits))))))
            (when-let [{:keys [x y base hits]} @marquee]
              (.stopPropagation e)
              (let [x2 (.-clientX e) y2 (.-clientY e)]
                (swap! marquee assoc :x2 x2 :y2 y2)
                (reset! key-selection
                        (vec (distinct (concat base
                          (mapcat (fn [{cx :x cy :y :keys [items]}]
                                    (when (and (<= (min x x2) cx (max x x2))
                                               (<= (min y y2) cy (max y y2))) items)) hits))))))))
          :on-pointer-up (fn [e]
                           (when-let [{:keys [x2 base hits]} @sweep]
                             (reset! sweep nil)
                             (when x2
                               (.stopPropagation e)
                               (mark-swept! (.-currentTarget e) [])
                               (reset! swept? true)
                               (rf/dispatch [::ui/select-many (into (vec base) hits)])))
                           (when @marquee (.stopPropagation e) (reset! marquee nil)))
          :on-pointer-cancel (fn [e]
                               (when @sweep
                                 (reset! sweep nil)
                                 (mark-swept! (.-currentTarget e) []))
                               (when @marquee (reset! key-selection (:base @marquee)) (reset! marquee nil)))
          :tab-index 0
          :on-key-down
          (fn [e]
            (cond
              (and (or (.-metaKey e) (.-ctrlKey e)) (= "a" (.toLowerCase (.-key e))))
              (do (.preventDefault e) (.stopPropagation e)
                  (reset! key-selection (vec (distinct (mapcat #(aget % "arthurKeys")
                    (array-seq (.querySelectorAll (.-currentTarget e) "button.tl-key")))))))
              (and (seq @key-selection) (#{"ArrowLeft" "ArrowRight" "Delete" "Backspace" "Escape"} (.-key e)))
              (do (.preventDefault e) (.stopPropagation e)
                  (let [k (.-key e) xs @key-selection]
                    (cond
                      (= k "Escape") (reset! key-selection [])
                      (#{"Delete" "Backspace"} k) (do (rf/dispatch [::ui/edit-keyframes xs nil]) (reset! key-selection []))
                      :else (let [df (* (if (= k "ArrowLeft") -1 1) (if (.-shiftKey e) 10 1))]
                              (rf/dispatch [::ui/edit-keyframes xs df])
                              (reset! key-selection (keyframes/shifted xs df))))))))
          :on-click (fn [^js e]
                      (cond
                        @swept? (reset! swept? false)
                        (= (.-target e) (.-currentTarget e)) (rf/dispatch [::ui/select nil])))
          :on-drag-enter (fn [^js e] (when (drag/accepts?) (.preventDefault e)))
          :on-drag-over (fn [^js e]
                          (when (drag/accepts?)
                            (.preventDefault e)
                            (drag/hover! :timeline (frame-at e frames) nil)))
          :on-drag-leave (fn [^js e]
                           (let [box (.getBoundingClientRect (.-currentTarget e))
                                 inside? (and (<= (.-left box) (.-clientX e) (.-right box))
                                              (<= (.-top box) (.-clientY e) (.-bottom box)))]
                             ;; Adding the inline ghost changes the element below
                             ;; the pointer and Chromium reports a leave with no
                             ;; related target. Keep the preview while the pointer
                             ;; is still geometrically inside the tracks.
                             (when (and (not inside?)
                                        (not (.contains (.-currentTarget e)
                                                        (.-relatedTarget e))))
                               (rf/dispatch [::ui/drop-clear]))))
          ;; In its own coordinates: dropped in time, nowhere in particular in
          ;; space, so what was drawn at a place stays at that place.
          :on-drop (fn [^js e]
                     (.preventDefault e)
                     (drag/land! (frame-at e frames) nil))
          ;; The rows and ruler share this exact major interval. Keeping a
          ;; second, hard-coded five-frame grid here made its lines disagree
          ;; with the numbered marks whenever the project's FPS changed.
          :ref #(when % (.observe sizer %))
          :class (when (< frame-px 4) "narrow")
          :style {"--tick" (str (* 100 (/ step frames)) "%")
                  "--fine" (if fine (str (* 100 (/ fine frames)) "%") "none")}}
         [:div.tl-ruler
          {:on-pointer-down (fn [^js e]
                              (rf/dispatch [::pb/seek (frame-at e frames)])
                              (reset! scrubbing true)
                              ;; Capture is what keeps a drag scrubbing once it
                              ;; leaves the ruler, and it is an ENHANCEMENT: it
                              ;; throws on a pointer id the browser does not have
                              ;; an active pointer for, which is every event
                              ;; `test/browser` synthesises. Seeking already
                              ;; happened, so the catch loses the drag and nothing
                              ;; else — where letting it throw would put an
                              ;; uncaught error on the console that the suite
                              ;; rightly fails on.
                              (try
                                (.setPointerCapture (.-currentTarget e) (.-pointerId e))
                                (catch :default _ nil)))
           :on-pointer-move (fn [^js e]
                              (when @scrubbing
                                (rf/dispatch [::pb/seek (frame-at e frames)])))
           :on-pointer-up     (fn [_] (reset! scrubbing false))
           :on-pointer-cancel (fn [_] (reset! scrubbing false))}
          (doall
           (for [f (range 0 frames step)]
             ^{:key f} [:div.tick {:style {:left (edge% f frames)}} f]))
          [outside-marks frames true]
          [playhead :div.tl-knob frames]]
         (if (seq visible)
           (doall (for [row visible]
                    (with-meta (if (= :section (:kind row))
                                 [:div.tl-track.tl-section]
                                 [track-cell row frames sliding hint
                                  {:clip clip :store store :open open
                                   :selection selection :selections selections
                                   :target-path target-path :key-selection key-selection :key-preview key-preview
                                   :key-anchor key-anchor :key-state key-state :key-elements key-elements :choose-row! choose-row!}])
                      {:key (str (:path row))})))
           [:div.tl-empty "nothing in this symbol"])
         ;; Over the rows, through the cels, as Flash draws its frames: a frame
         ;; is a cell you can see wherever there is time.
         [:div.tl-grid {:class (when-not fine "coarse")}]
         [outside-marks frames false]
         [playhead :div.tl-playhead frames]
         [key-marquee marquee]
         [key-marquee sweep]]]
      [cursor-hint hint]])
    (finally (.disconnect sizer))))

(defn view []
  [timeline-view])
